'use client'

import { Card, CardContent, CardHeader, CardTitle, CardFooter } from 'ui/card'
import { Button } from 'ui/button'
import { EaPlayerStats } from 'lib/proclubs/shared'
import { getAssetFullPath } from 'app/helpers'
import Image from 'next/image'
import { useTranslation } from 'components/providers/TranslationProvider'

interface ProclubsPlayerStatsCardProps {
    player: EaPlayerStats
    onClose: () => void
}

const StatsCard = ({
    title,
    icon,
    value,
    size
}: {
    title: string
    icon: string
    value?: string | number | null
    size?: number
}) => {
    return (
        <Card className='flex flex-col justify-between pb-5 w-full h-[210px]'>
            <CardHeader className='text-center'>{title}</CardHeader>
            <CardContent className='mx-auto flex w-fit'>
                <Image src={icon} alt={title} width={size || 48} height={size || 48} />
            </CardContent>
            <CardFooter>
                <p className='font-bold text-[18px] mx-auto w-fit'>{value ?? '0'}</p>
            </CardFooter>
        </Card>
    )
}

const ProclubsPlayerStatsCard = ({ player, onClose }: ProclubsPlayerStatsCardProps) => {
    const { t, isLoading: translationsLoading } = useTranslation()
    const basePath = getAssetFullPath('/images/stats-icons/')

    if (translationsLoading || !t) {
        return null
    }

    // Helper to get numeric value or default to 0
    const getValue = (val: string | undefined | null): string => {
        if (!val || val === '' || val === '-') return '0'
        return val
    }

    // Helper to get numeric value for display (handles decimals)
    const getNumericValue = (val: string | undefined | null): string => {
        if (!val || val === '' || val === '-') return '0'
        const num = parseFloat(val)
        if (isNaN(num)) return '0'
        return num % 1 === 0 ? num.toString() : num.toFixed(1)
    }

    // Use EA's raw values - don't calculate percentages
    // Only show stats that EA actually provides
    const stats = [
        {
            title: t('overAllMatchRating') || 'Overall Match Rating',
            icon: `${basePath}rating.png`,
            value: getNumericValue(player.rating)
        },
        {
            title: t('goals') || 'Goals',
            icon: `${basePath}goals.png`,
            value: getValue(player.goals)
        },
        {
            title: t('assists') || 'Assists',
            icon: `${basePath}assists.png`,
            value: getValue(player.assists)
        },
        {
            title: 'Shots',
            icon: `${basePath}goals.png`,
            value: getValue(player.shots)
        },
        {
            title: 'Passes Made',
            icon: `${basePath}accuracy.png`,
            value: getValue(player.passesmade)
        },
        {
            title: 'Pass Attempts',
            icon: `${basePath}accuracy.png`,
            value: getValue(player.passattempts)
        },
        {
            title: 'Tackles Made',
            icon: `${basePath}tackle-success.png`,
            value: getValue(player.tacklesmade)
        },
        {
            title: 'Tackle Attempts',
            icon: `${basePath}tackle-success.png`,
            value: getValue(player.tackleattempts)
        },
        {
            title: t('saves') || 'Saves',
            icon: `${basePath}saves.png`,
            value: getValue(player.saves),
            size: 40
        },
        {
            title: t('goalsConceded') || 'Goals Conceded',
            icon: `${basePath}goals.png`,
            value: getValue(player.goalsconceded)
        },
        {
            title: t('cleanSheets') || 'Clean Sheets (Any)',
            icon: `${basePath}clean-sheet.png`,
            value: getValue((player as any).cleansheetsany)
        },
        {
            title: 'Clean Sheets (Def)',
            icon: `${basePath}clean-sheet.png`,
            value: getValue((player as any).cleansheetsdef)
        },
        {
            title: 'Clean Sheets (GK)',
            icon: `${basePath}clean-sheet.png`,
            value: getValue((player as any).cleansheetsgk)
        },
        {
            title: t('redCards') || 'Red Cards',
            icon: `${basePath}yellow-card.png`,
            value: getValue((player as any).redcards),
            size: 40
        },
        {
            title: 'Ball Dive Saves',
            icon: `${basePath}saves.png`,
            value: getValue((player as any).ballDiveSaves),
            size: 40
        },
        {
            title: 'Cross Saves',
            icon: `${basePath}saves.png`,
            value: getValue((player as any).crossSaves),
            size: 40
        },
        {
            title: 'Good Direction Saves',
            icon: `${basePath}saves.png`,
            value: getValue((player as any).goodDirectionSaves),
            size: 40
        },
        {
            title: 'Parry Saves',
            icon: `${basePath}saves.png`,
            value: getValue((player as any).parrySaves),
            size: 40
        },
        {
            title: 'Punch Saves',
            icon: `${basePath}saves.png`,
            value: getValue((player as any).punchSaves),
            size: 40
        },
        {
            title: 'Reflex Saves',
            icon: `${basePath}saves.png`,
            value: getValue((player as any).reflexSaves),
            size: 40
        },
        {
            title: 'Wins',
            icon: `${basePath}rating.png`,
            value: getValue((player as any).wins)
        },
        {
            title: 'Losses',
            icon: `${basePath}rating.png`,
            value: getValue((player as any).losses)
        },
        {
            title: 'Score',
            icon: `${basePath}rating.png`,
            value: getValue((player as any).SCORE)
        },
        {
            title: 'Man of the Match',
            icon: `${basePath}rating.png`,
            value: player.mom === '1' ? 'Yes' : 'No'
        },
        {
            title: 'Seconds Played',
            icon: `${basePath}rating.png`,
            value: getValue(player.secondsPlayed || (player as any).gameTime)
        },
    ]

    return (
        <Card>
            <CardHeader>
                <div className='flex items-center justify-between'>
                    <CardTitle>
                        {player.playername || 'Unknown Player'} - {t('playerStats') || 'Player Stats'}
                    </CardTitle>
                    <Button
                        variant='ghost'
                        size='sm'
                        onClick={onClose}
                    >
                        Close
                    </Button>
                </div>
            </CardHeader>
            <CardContent>
                <div className='w-full'>
                    <div className='flex flex-wrap justify-center gap-2'>
                        {stats.map((stat, index) => (
                            <div
                                key={index}
                                className='w-[calc(50%-0.5rem)] md:w-[calc(33.333%-0.667rem)] lg:w-[calc(25%-0.75rem)]'
                            >
                                <StatsCard
                                    title={stat.title}
                                    icon={stat.icon}
                                    value={stat.value}
                                    size={stat.size}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            </CardContent>
        </Card>
    )
}

export default ProclubsPlayerStatsCard

